import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, auth } from '../api/client.ts';
import type { CompanyProfile, SessionUser } from '../api/client.ts';

/**
 * Estado de sessão do painel.
 *
 * A verdade sobre "estou logado" mora no cookie httpOnly, que o JavaScript não
 * enxerga. Este contexto guarda apenas o resultado do `GET /auth/me` — ou seja,
 * o que o servidor confirmou. Guardar um booleano próprio em localStorage
 * criaria um segundo estado que diverge do real assim que a sessão expira.
 */

interface AuthState {
  user: SessionUser | null;
  /** Perfis de empresa do workspace do usuário — a lista do seletor (Fase 11). */
  profiles: CompanyProfile[];
  /** True até a primeira resposta do /auth/me. Evita piscar a tela de login. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchProfile: (profileId: string) => Promise<void>;
  /** Recarrega a lista de perfis — chamado depois de criar/editar um na tela de perfis. */
  refreshProfiles: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [profiles, setProfiles] = useState<CompanyProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    auth
      .me()
      .then((data) => {
        if (!active) return;
        setUser(data.user);
        setProfiles(data.profiles);
      })
      .catch((error: unknown) => {
        // 401 aqui é o caso normal de quem ainda não entrou, não um problema.
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.error('Falha ao verificar sessão:', error);
        }
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // A sessão expirou no meio do uso (qualquer chamada voltou 401): sem isto a
  // tela ficava aberta, com cada clique falhando, até alguém recarregar.
  // Zerar o usuário faz o `RequireAuth` levar ao login guardando a rota.
  useEffect(() => {
    const expired = () => {
      setUser(null);
      setProfiles([]);
    };
    window.addEventListener('prospector:unauthorized', expired);
    return () => window.removeEventListener('prospector:unauthorized', expired);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await auth.login(email, password);
    setUser(data.user);
    const me = await auth.me();
    setProfiles(me.profiles);
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      // Limpa o estado local mesmo se a chamada falhar: se o usuário pediu para
      // sair, deixá-lo com a tela aberta é o pior resultado possível.
      setUser(null);
      setProfiles([]);
    }
  }, []);

  const switchProfile = useCallback(async (profileId: string) => {
    await auth.switchProfile(profileId);
    setUser((current) => (current ? { ...current, activeProfileId: profileId } : current));
  }, []);

  const refreshProfiles = useCallback(async () => {
    const me = await auth.me();
    setProfiles(me.profiles);
  }, []);

  const value = useMemo(
    () => ({ user, profiles, loading, login, logout, switchProfile, refreshProfiles }),
    [user, profiles, loading, login, logout, switchProfile, refreshProfiles],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}
